// Session Management Utility for Admin Portal
// Handles session expiration, validation, and automatic logout

const SESSION_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_BEFORE_EXPIRY = 5 * 60 * 1000; // Show warning 5 minutes before expiry

/**
 * Creates an admin session with expiration time
 */
export const createAdminSession = (adminData) => {
    const sessionData = {
        ...adminData,
        loginTime: Date.now(),
        expiresAt: Date.now() + SESSION_DURATION,
        lastActivity: Date.now()
    };
    localStorage.setItem('adminSession', JSON.stringify(sessionData));
    return sessionData;
};

/**
 * Gets the current admin session
 */
export const getAdminSession = () => {
    try {
        const sessionStr = localStorage.getItem('adminSession');
        if (!sessionStr) return null;

        const session = JSON.parse(sessionStr);
        return session;
    } catch (error) {
        console.error('Error reading admin session:', error);
        return null;
    }
};

/**
 * Updates last activity timestamp to extend session
 */
export const updateLastActivity = () => {
    const session = getAdminSession();
    if (session) {
        session.lastActivity = Date.now();
        localStorage.setItem('adminSession', JSON.stringify(session));
    }
};

/**
 * Checks if the session is valid (not expired)
 */
export const isSessionValid = () => {
    const session = getAdminSession();
    if (!session) return false;

    const now = Date.now();
    return now < session.expiresAt;
};

/**
 * Gets remaining time in the session (in milliseconds)
 */
export const getRemainingTime = () => {
    const session = getAdminSession();
    if (!session) return 0;

    const now = Date.now();
    const remaining = session.expiresAt - now;
    return remaining > 0 ? remaining : 0;
};

/**
 * Checks if session is about to expire (within warning threshold)
 */
export const isSessionExpiringSoon = () => {
    const remaining = getRemainingTime();
    return remaining > 0 && remaining <= WARNING_BEFORE_EXPIRY;
};

/**
 * Extends the current session by creating a new expiration time
 */
export const extendSession = () => {
    const session = getAdminSession();
    if (session) {
        session.expiresAt = Date.now() + SESSION_DURATION;
        session.lastActivity = Date.now();
        localStorage.setItem('adminSession', JSON.stringify(session));
        return true;
    }
    return false;
};

/**
 * Destroys the admin session (logout)
 */
export const destroyAdminSession = () => {
    localStorage.removeItem('adminSession');
};

/**
 * Formats remaining time for display
 */
export const formatRemainingTime = () => {
    const remaining = getRemainingTime();
    if (remaining <= 0) return 'Expired';

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (minutes > 0) {
        return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
};

/**
 * Hook-like function to check session on component mount
 * Returns: { valid: boolean, session: object, remaining: number }
 */
export const validateSession = () => {
    const session = getAdminSession();
    const valid = isSessionValid();
    const remaining = getRemainingTime();

    return {
        valid,
        session,
        remaining,
        expiringSoon: isSessionExpiringSoon()
    };
};

const sessionManager = {
    createAdminSession,
    getAdminSession,
    updateLastActivity,
    isSessionValid,
    getRemainingTime,
    isSessionExpiringSoon,
    extendSession,
    destroyAdminSession,
    formatRemainingTime,
    validateSession,
    SESSION_DURATION,
    WARNING_BEFORE_EXPIRY
};

export default sessionManager;
